import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/react/sortable";

import PopoverRadioMenu from "../../../../../../../components/Inputs/PopoverRadioMenu";
import { formatAddress } from "../../../../RoutePage/Routes/utils";
import { DragDropIcon } from "../../../../RoutePage/UI/DragDropIcon";
import {
  LockPosition,
  RouteBuilderProperty,
} from "../../../../../../../types/routeBuilder";
import { serviceTypeLabel } from "../../../lib/serviceHelpers";
import {
  formatEstimatedServiceTimeFromSeconds,
  incorporatesTime,
} from "../../../lib/helpers";
import {
  UNASSIGNED_ROUTE_ID,
  useRouteBuilder,
} from "../../../context/RouteBuilderContext";
import { EdgeLockPosition } from "../../../lib/lockUtils";
import { multipleChoiceConfirmWrapper } from "../../../../../../../components/Modals/MultipleChoiceConfirmWrapper";
import LockFilledIcon from "../../../../../../../resources/route-builder/lock-filled.svg";
import LockOutlineIcon from "../../../../../../../resources/route-builder/lock-outline.svg";
import UnassignedTriangleIcon from "../../../../../../../resources/route-builder/unassigned-triangle.svg";
import XMarkIcon from "../../../../../../../resources/route-builder/x-mark.svg";

const RECENT_SERVICE_HOURS = 6;

function isRecentlyServiced(
  lastServicedAt: string | undefined,
  dispatchTime: string,
  hours = RECENT_SERVICE_HOURS,
) {
  if (!lastServicedAt) return false;
  const dispatch = new Date(dispatchTime).getTime();
  const lastServiced = new Date(lastServicedAt).getTime();
  if (Number.isNaN(dispatch) || Number.isNaN(lastServiced)) return false;
  const ageMs = dispatch - lastServiced;
  return ageMs >= 0 && ageMs <= hours * 60 * 60 * 1000;
}

function LockIcon({ filled }: { filled?: boolean }) {
  return (
    <img
      src={filled ? LockFilledIcon : LockOutlineIcon}
      alt=""
      className="h-3.5 w-3.5"
      aria-hidden
    />
  );
}

function lockSummary(lock: LockPosition) {
  return lock == null ? "Free to move" : "Locked in place";
}

const MAX_SERVICE_BADGES = 3;

type StopCardProps = {
  property: RouteBuilderProperty;
  propertyVisitId: string;
  index: number;
  routeId: string;
  colorClass: string;
  lockControlPosition: EdgeLockPosition | null;
  highlight?: boolean;
  onOpen: () => void;
  onLockChange: (lock: LockPosition) => void;
  onRemove: () => void;
};

type DraggableStopCardProps = StopCardProps & {
  lockMenuOpen: boolean;
  setLockMenuOpen: (open: boolean) => void;
};

export default function StopCard(props: StopCardProps) {
  const [lockMenuOpen, setLockMenuOpen] = useState(false);
  const { ref, isDragging } = useSortable({
    id: props.propertyVisitId,
    index: props.index,
    group: props.routeId,
    type: "stop",
    collisionPriority: 20,
    // Keep React's list item in place while dnd-kit draws the drag feedback.
    feedback: "clone",
    disabled: lockMenuOpen,
    data: {
      routeId: props.routeId,
      propertyVisitId: props.propertyVisitId,
      lock: props.property.lock,
    },
  });
  return (
    <StopCardContent
      {...props}
      lockMenuOpen={lockMenuOpen}
      setLockMenuOpen={setLockMenuOpen}
      dragRef={ref}
      isDragging={isDragging}
    />
  );
}

function StopCardContent({
  property,
  propertyVisitId,
  index,
  routeId,
  colorClass,
  lockControlPosition,
  highlight,
  onOpen,
  onLockChange,
  onRemove,
  dragRef,
  isDragging,
  lockMenuOpen,
  setLockMenuOpen,
}: DraggableStopCardProps & {
  dragRef: (node: HTMLElement | null) => void;
  isDragging: boolean;
}) {
  const {
    session,
    viewMode,
    listRevealNonce,
    unassignPropertyVisit,
    confirmMoveToUnassigned,
  } = useRouteBuilder();
  const isTemporary = session?.inputs.routeType === "temporary";
  const urgent =
    isTemporary &&
    isRecentlyServiced(
      property.lastServicedAt,
      session?.inputs.plannedDispatchTime ?? "",
    );

  const [flashing, setFlashing] = useState(false);
  const cardNodeRef = useRef<HTMLElement | null>(null);
  const lock = property.lock ?? null;
  const isLocked = lock === "start" || lock === "end";
  const isUnassigned = routeId === UNASSIGNED_ROUTE_ID;

  const summary = lockSummary(lock);
  const movesToUnassigned =
    !isUnassigned &&
    (session?.builderMode === "edit" || session?.origin !== "blank");
  // Every ticked service, not just the first. Past three, the rest go in a "+N" badge.
  // Skip empty entries, which a draft saved from older data can hold.
  const tickedServices = property.serviceTypes?.filter(Boolean) ?? [];
  const services =
    tickedServices.length > 0 ? tickedServices : [property.serviceType];
  const shownServices =
    services.length > MAX_SERVICE_BADGES
      ? services.slice(0, MAX_SERVICE_BADGES - 1)
      : services;
  const hiddenServices = services.slice(shownServices.length);
  const lockOptions: Array<{
    value: LockPosition;
    label: string;
    description: string;
  }> = [
    {
      value: lockControlPosition,
      label: "Locked in place",
      description: "Keep this property fixed when re-ordering or optimizing",
    },
    {
      value: null,
      label: "Free to move",
      description: "Allow this property to be re-ordered or optimized",
    },
  ];

  const setCardRef = useCallback(
    (node: HTMLElement | null) => {
      dragRef(node);
      cardNodeRef.current = node;
    },
    [dragRef],
  );

  const confirmStopAction = async () => {
    if (movesToUnassigned) {
      return confirmMoveToUnassigned({
        name: property.name,
        locked: isLocked,
      });
    }
    return multipleChoiceConfirmWrapper("Remove property visit?", {
      description: `Remove this visit to ${property.name} from the plan? You will need to add the property in again yourself if you want it in a route.`,
      confirmLabel: "Remove",
    });
  };

  useEffect(() => {
    if (!highlight || viewMode !== "split" || listRevealNonce === 0) return;
    cardNodeRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
    setFlashing(true);
    const timer = window.setTimeout(() => setFlashing(false), 650);
    return () => {
      window.clearTimeout(timer);
      setFlashing(false);
    };
  }, [highlight, listRevealNonce, viewMode]);

  return (
    <li
      ref={setCardRef}
      className={`relative list-none rounded-xl border bg-white p-2.5 shadow-sm transition cursor-grab active:cursor-grabbing ${
        highlight
          ? flashing
            ? "border-sky-500 ring-2 ring-sky-400"
            : "border-sky-500 ring-2 ring-sky-200"
          : "border-slate-200"
      } ${isDragging ? "opacity-60" : ""} ${lockMenuOpen ? "z-30" : "z-0"}`}
      data-testid={`stop-card-${propertyVisitId}`}
    >
      {lockControlPosition ? (
        <div
          onPointerDownCapture={(event) => event.stopPropagation()}
          onMouseDownCapture={(event) => event.stopPropagation()}
        >
          <PopoverRadioMenu<LockPosition>
            value={lock}
            options={lockOptions}
            onChange={onLockChange}
            summary={summary}
            menuTitle="Property movement"
            testIdPrefix="stop-lock"
            idSuffix={propertyVisitId}
            open={lockMenuOpen}
            onOpenChange={setLockMenuOpen}
            triggerTitle={`${summary}. Click to change property movement.`}
            triggerAriaLabel={`${summary}. Open property movement menu`}
            triggerClassName={`flex h-6 items-center gap-0.5 rounded px-1 ${
              isLocked
                ? "bg-violet-100 text-violet-700"
                : "bg-white/90 text-slate-400 hover:text-slate-600"
            }`}
            triggerContent={
              <>
                <LockIcon filled={isLocked} />
                <span className="text-[9px] font-semibold leading-none">▾</span>
              </>
            }
          />
        </div>
      ) : isLocked ? (
        <span
          role="img"
          aria-label="Locked in place"
          title="Locked in place"
          data-testid={`stop-lock-indicator-${propertyVisitId}`}
          className="absolute right-1 top-1 z-20 flex h-6 items-center rounded bg-violet-100 px-1.5 text-violet-700"
        >
          <LockIcon filled />
        </span>
      ) : null}

      <div className="flex gap-2">
        <div
          className="mt-0.5 touch-none cursor-grab"
          title={
            isLocked ? "Drag to Unassigned to clear its lock" : "Drag to move"
          }
        >
          <DragDropIcon />
        </div>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            className="w-full cursor-inherit pr-10 text-left"
            onClick={onOpen}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span
                  className={`relative flex shrink-0 items-center justify-center font-bold text-white ${
                    isUnassigned
                      ? `h-8 w-8 ${index + 1 > 9 ? "text-[10px]" : "text-xs"}`
                      : `h-6 w-6 rounded-full text-xs ${
                          urgent
                            ? "bg-violet-600 ring-[2.5px] ring-red-500"
                            : colorClass
                        }`
                  }`}
                  role={isUnassigned ? "img" : undefined}
                  aria-label={
                    isUnassigned ? `Unassigned job ${index + 1}` : undefined
                  }
                  data-testid={
                    urgent && !isUnassigned
                      ? `stop-urgent-badge-${property.jobId}`
                      : `stop-index-badge-${property.jobId}`
                  }
                >
                  {isUnassigned ? (
                    <img
                      src={UnassignedTriangleIcon}
                      alt=""
                      className="absolute inset-0 h-full w-full"
                      aria-hidden
                    />
                  ) : null}
                  <span className="relative">{index + 1}</span>
                </span>
                <span className="truncate text-sm font-semibold text-snow-primary">
                  {property.name}
                </span>
              </div>
            </div>
            <p className="mt-0.5 truncate text-xs text-slate-600">
              {formatAddress(property.address)}
            </p>
          </button>
          <div className="-mr-1.5 mt-1.5 flex items-center gap-2">
            <button
              type="button"
              className="flex min-w-0 flex-1 cursor-inherit flex-wrap gap-1 text-left text-[10px] font-medium"
              onClick={onOpen}
            >
              {shownServices.map((service) => (
                <span
                  key={service}
                  className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700"
                >
                  {serviceTypeLabel(service)}
                </span>
              ))}
              {hiddenServices.length > 0 ? (
                <span
                  className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700"
                  title={hiddenServices
                    .map((service) => serviceTypeLabel(service))
                    .join(", ")}
                >
                  +{hiddenServices.length}
                </span>
              ) : null}
              {incorporatesTime(session?.inputs) ? (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">
                  {formatEstimatedServiceTimeFromSeconds(
                    property.estimatedServiceTimeSeconds,
                  )}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              data-testid={
                movesToUnassigned
                  ? `unassign-property-visit-${propertyVisitId}`
                  : `remove-property-visit-${propertyVisitId}`
              }
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600"
              aria-label={
                movesToUnassigned
                  ? `Move visit to ${property.name} to Unassigned`
                  : `Remove visit to ${property.name}`
              }
              title={
                movesToUnassigned
                  ? "Move to Unassigned"
                  : "Remove property visit"
              }
              onPointerDownCapture={(event) => event.stopPropagation()}
              onClick={async (event) => {
                event.stopPropagation();
                const confirmed = await confirmStopAction();
                if (!confirmed) return;
                if (movesToUnassigned) {
                  unassignPropertyVisit(propertyVisitId);
                } else {
                  onRemove();
                }
              }}
            >
              <img src={XMarkIcon} alt="" className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}
